import React, { useEffect, useState } from 'react';
import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  onSnapshot,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { app, db } from '../../firebase/firebaseConfig';
import {
  HCard,
  HPrimaryButton,
  HSecondaryButton,
  SectionLabel,
  HearthIcon,
} from '../h';
import { useMock } from './mockContext';

/* Incoming Hearth (Co-Manager) invites.

   Subscribes live to `familyInviteMessages/{userEmail}/familyIDs` (the
   notifier subcollection the OLD webapp uses for Hearth-level invites).
   For each pending familyID we fetch the family's display name one-shot
   (the sender's own family name — the same name the OLD acceptFamilyInvite
   writes into the invitee's side).

   Accept (matches OLD IndividualDashboard.acceptFamilyInvite, 180–233):
     1. Delete familyInviteMessages/{email}/familyIDs/{familyID}
     2. If no siblings remain, delete parent familyInviteMessages/{email}
     3. Delete familyInfo/{familyID}/pendingControllerInvites/{email}
     4. Add familyInfo/{familyID}/controllers/{accountUUID} (UUID-keyed,
        while email-keyed still exists so the isController rule passes)
     5. Delete familyInfo/{familyID}/controllers/{email}
     6. accountsInfo/{accountUUID}: arrayUnion(families, familyID) +
        numOfFamilies+1 + numOfInvitedFamilies+1

   Decline (matches OLD IndividualDashboard.declineFamilyInvite, 235–290):
     1. Delete the invite notifier doc (and parent if last)
     2. Delete the pendingControllerInvites doc
     3. Delete both controllers/{email} and controllers/{accountUUID}
        (ignore NOT_FOUND on either — we're just making sure it's gone)
     4. arrayRemove self from accountsInfo.families; if the families array
        becomes empty, delete the accountsInfo doc and sign out (the user
        declined their only invite and doesn't otherwise have an account).
*/
export function IncomingHearthInvites({ userEmail, accountUUID }) {
  const mock = useMock();
  const [inviteFamilyIDs, setInviteFamilyIDs] = useState([]);
  const [inviteDetails, setInviteDetails] = useState({});

  // Live subscription on the notifier collection. As the owner cancels or
  // as we accept/decline, entries appear/disappear here.
  useEffect(() => {
    if (mock || !userEmail) return undefined;
    const unsub = onSnapshot(
      collection(db, 'familyInviteMessages', userEmail, 'familyIDs'),
      (snap) => {
        setInviteFamilyIDs(snap.docs.map((d) => d.id));
      },
      (err) => {
        if (err?.code !== 'permission-denied') {
          console.warn('IncomingHearthInvites notifier listener error', err.code);
        }
      },
    );
    return () => unsub();
  }, [userEmail, mock]);

  // For each pending familyID, resolve the Hearth's display name.
  useEffect(() => {
    if (mock || !userEmail) return undefined;
    let cancelled = false;
    (async () => {
      const next = {};
      for (const familyID of inviteFamilyIDs) {
        try {
          const snap = await getDoc(doc(db, 'familyInfo', familyID));
          if (snap.exists()) {
            next[familyID] = { familyName: snap.data().name || 'a Hearth' };
          } else {
            next[familyID] = { familyName: 'a Hearth' };
          }
        } catch (err) {
          if (err?.code !== 'permission-denied') {
            console.warn('IncomingHearthInvites detail fetch', familyID, err);
          }
        }
      }
      if (!cancelled) setInviteDetails(next);
    })();
    return () => { cancelled = true; };
  }, [inviteFamilyIDs, userEmail, mock]);

  if (!userEmail || inviteFamilyIDs.length === 0) return null;

  /* Accept a Hearth (Co-Manager) invite.

     Semantically matches OLD IndividualDashboard.acceptFamilyInvite but
     batches the five writes that can be batched so listeners on both the
     invitee's side (accountsInfo, notifier collection) and the inviter's
     side (familyInfo/{fid}/controllers) see a single consistent snapshot.
     Issuing them as independent sequential writes fires six separate
     watch-stream events, which under Firestore JS SDK 11.0.1 has been
     known to hit an "INTERNAL ASSERTION FAILED: Unexpected state" when the
     same listener storm coincides with listener re-subscriptions caused
     by accountsInfo.families growing.

     The parent familyInviteMessages/{email} cleanup needs a read-then-
     write, so it runs after the batch commit (if all children are gone).
     We don't navigate away — the newly-accepted Hearth appears in the
     list on this tab naturally, and avoiding a route change while fresh
     listeners are attaching reduces the listener churn further. */
  const accept = async (familyID) => {
    try {
      if (accountUUID) {
        const batch = writeBatch(db);
        batch.delete(
          doc(db, 'familyInviteMessages', userEmail, 'familyIDs', familyID),
        );
        batch.delete(
          doc(db, 'familyInfo', familyID, 'pendingControllerInvites', userEmail),
        );
        // Add UUID-keyed controller BEFORE deleting the email-keyed one so
        // the isController() rule always sees at least one matching entry.
        batch.set(
          doc(db, 'familyInfo', familyID, 'controllers', accountUUID),
          {},
        );
        batch.delete(
          doc(db, 'familyInfo', familyID, 'controllers', userEmail),
        );
        batch.update(doc(db, 'accountsInfo', accountUUID), {
          families: arrayUnion(familyID),
          numOfFamilies: increment(1),
          numOfInvitedFamilies: increment(1),
        });
        await batch.commit();
      } else {
        // No accountUUID claim yet (brand-new sign-in race). Minimal clean.
        await deleteDoc(
          doc(db, 'familyInviteMessages', userEmail, 'familyIDs', familyID),
        );
        await deleteDoc(
          doc(db, 'familyInfo', familyID, 'pendingControllerInvites', userEmail),
        ).catch(() => {});
        await deleteDoc(
          doc(db, 'familyInfo', familyID, 'controllers', userEmail),
        ).catch(() => {});
      }
      // Parent familyInviteMessages/{email} cleanup: needs a read-then-
      // write so it can't live in the batch. Runs after the batch commits.
      try {
        const remaining = await getDocs(
          collection(db, 'familyInviteMessages', userEmail, 'familyIDs'),
        );
        if (remaining.empty) {
          await deleteDoc(doc(db, 'familyInviteMessages', userEmail)).catch(() => {});
        }
      } catch (_) {}
    } catch (err) {
      console.error('IncomingHearthInvites accept error', familyID, err);
    }
  };

  const decline = async (familyID) => {
    try {
      await deleteDoc(
        doc(db, 'familyInviteMessages', userEmail, 'familyIDs', familyID),
      );
      const remaining = await getDocs(
        collection(db, 'familyInviteMessages', userEmail, 'familyIDs'),
      );
      if (remaining.empty) {
        await deleteDoc(doc(db, 'familyInviteMessages', userEmail));
      }
      await deleteDoc(
        doc(db, 'familyInfo', familyID, 'pendingControllerInvites', userEmail),
      );
      await deleteDoc(
        doc(db, 'familyInfo', familyID, 'controllers', userEmail),
      ).catch(() => {});
      if (accountUUID) {
        await deleteDoc(
          doc(db, 'familyInfo', familyID, 'controllers', accountUUID),
        ).catch(() => {});
        await updateDoc(doc(db, 'accountsInfo', accountUUID), {
          families: arrayRemove(familyID),
          numOfFamilies: increment(-1),
          numOfInvitedFamilies: increment(-1),
        }).catch(() => {});
        // If declining left the user with no families at all, their
        // account has nothing to manage — follow OLD behaviour and tear
        // down the account + force a sign-out.
        try {
          const accountSnap = await getDoc(doc(db, 'accountsInfo', accountUUID));
          if (accountSnap.exists()) {
            const fams = accountSnap.data().families || [];
            if (fams.length === 0) {
              await deleteDoc(doc(db, 'accountsInfo', accountUUID));
              const auth = getAuth(app);
              if (auth.currentUser) {
                try { await auth.currentUser.delete(); } catch (_) {}
              }
              window.location.href = '/';
            }
          }
        } catch (_) {}
      }
    } catch (err) {
      console.error('IncomingHearthInvites decline error', familyID, err);
    }
  };

  return (
    <>
      <SectionLabel className="mb-2">Hearth invites</SectionLabel>
      <div className="space-y-3 mb-4">
        {inviteFamilyIDs.map((familyID) => {
          const d = inviteDetails[familyID];
          if (!d) return null;
          return (
            <HCard key={familyID} className="p-4">
              <div className="flex items-start gap-3 mb-3">
                <HearthIcon size="sm" className="flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-semibold text-slate-900 truncate">
                    {d.familyName}
                  </div>
                  <div className="text-[12px] text-slate-500 leading-relaxed mt-0.5">
                    You&rsquo;ve been invited to help manage this Hearth as a
                    Co-Manager.
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <HSecondaryButton onClick={() => decline(familyID)}>
                  Decline
                </HSecondaryButton>
                <HPrimaryButton onClick={() => accept(familyID)}>
                  Accept
                </HPrimaryButton>
              </div>
            </HCard>
          );
        })}
      </div>
    </>
  );
}
